import { GetLocationConfig } from "./location/pcMapConfig";
import { Emitter } from "Util/emitter";
import { ClientLocation } from "Util/clientLocation";
import { EMBED_APP_SYMBOL, PAGE_APP_SYMBOL, PC_RES_SYMBOL } from "_Main/injectionSymbols";
import { geocodeAddressUsingAzureMaps } from "./location/geocoding";
import { GetAddressFromDependency, IsAddressCompleteEnoughForAutoGeocode } from "./location/locationUtil";
import { PROJECT_FORM_CONTROLLER_TYPES } from "../ProjectForm/projectFormControllerStore";
import { PROJECT_FORM_TYPES, PROJECT_FORM_NAMESPACE, PROJECT_FORM_MODE } from "../ProjectForm/projectFormStore";
import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
import { useResource } from "Util/i18n";
import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
import { ValidationHub } from "Validation/validationHub";
import { KEYS, NAMESPACE } from "./i18n/location.i18n";
import { useProjectLockStore } from "Projects/projectLockStore.js";

import { ref, computed, watch, inject } from "vue";
import { isModuleRegistered } from "SharedStores/util";
import { simpleDebounce, getBooleanPropertyValue } from "Util/util";
import { ELEMENT_PROPERTY_NAME } from "Types/elementTypes";
import { ALERT_SEVERITY } from "Util/alerts";

export function useLocationShared(store, element, locationDataFieldId, isInFormEditor) {
    // setup map emitter
    const mapEmitter = new Emitter();
    mapEmitter.on("chosen-location-change", updateLocationFromMap);
    mapEmitter.on("map-ready", setInitialLocation);

    const debouncedAutoGeocode = simpleDebounce(autoGeocodeFromDependency, 500);

    const dependencyChanged = ref(false);
    const suppressOverlay = ref(false);

    const LATITUDE_VALIDATOR_KEY = "latitude";
    const LONGITUDE_VALIDATOR_KEY = "longitude";

    const app = inject(PAGE_APP_SYMBOL, null) ?? inject(EMBED_APP_SYMBOL);
    const res = inject(PC_RES_SYMBOL);

    const overrideValue = ref(null);
    const sasToken = ref(null);
    const mapConfig = ref(null);

    const retrievingSasToken = ref(false);
    const editingOverrideManually = ref(false);
    const editingOverrideViaMap = ref(false);
    const mapServiceUnavailable = ref(false);

    const validationResults = ref({
        manualLat: null,
        manualLong: null
    });

    const validationHub = new ValidationHub();

    // versions of methods from _elementBase.js
    const projectLockStore = computed(() => {
        return useProjectLockStore();
    });

    const baseReadonly = computed(() => {
        const propVal = element.Properties[ELEMENT_PROPERTY_NAME.Readonly] || "";
        return (
            propVal.toLowerCase() === "true" ||
            projectLockStore.value.isLocked ||
            isView.value ||
            isImmutableSingleFieldElement.value
        );
    });

    const isImmutableSingleFieldElement = computed(() => {
        const locationDataField = store.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](locationDataFieldId);
        if (!locationDataField) {
            return false;
        }
        return locationDataField.IsImmutable;
    });

    const isView = computed(() => {
        const mode = projectFormMode.value;
        return (
            mode === PROJECT_FORM_MODE.View ||
            mode === PROJECT_FORM_MODE.Print ||
            mode === PROJECT_FORM_MODE.AdminReadonly
        );
    });

    const isPrefill = computed(() => {
        const mode = projectFormMode.value;
        return mode === PROJECT_FORM_MODE.Prefill || mode === PROJECT_FORM_MODE.PrefillAdmin;
    });

    const projectFormMode = computed(() => {
        if (isModuleRegistered(PROJECT_FORM_NAMESPACE, store.state)) {
            return store.state[PROJECT_FORM_NAMESPACE].ProjectFormMode;
        }
        return PROJECT_FORM_MODE.Edit;
    });
    //end of methods from _elementBase.js

    // i18n specific to methods/computeds in this file
    const {
        longitudeMaxLengthErrorMsg,
        longitudeMinLengthErrorMsg,
        longitudeLocationCompleteErrorMsg,
        latitudeMaxLengthErrorMsg,
        latitudeMinLengthErrorMsg,
        latitudeLocationCompleteErrorMsg,
        longitudeLabel,
        latitudeLabel,
        invitationToEditText,
        invitationToEditTextTextInFormEditor,
        dependencyLoaderMessageText,
        dependencyChangedAlertText,
        dependencyChangedButtonLabel,
        locateNowButtonLabel
    } = useResource(KEYS, NAMESPACE);

    const dependencyLoaderMessageNamed = computed(() => {
        return res({
            namespace: NAMESPACE,
            key: KEYS.dependencyLoaderMessageNamedText,
            values: { dependency: parentElementLabel.value }
        });
    });
    // end i18n

    const overrideReadonly = computed(() => {
        return baseReadonly.value || isPrefill.value;
    });

    const enableLocationMapProgramSwitch = computed(() => {
        return store.state.program.EnableLocationMap;
    });

    const showLocationMap = computed(() => {
        if (enableLocationMapProgramSwitch.value) {
            if (ELEMENT_PROPERTY_NAME.ShowLocationMap in element.Properties) {
                return getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.ShowLocationMap, element);
            } else {
                return true;
            }
        }
        return false;
    });

    const usingLocationMap = computed(() => {
        return !isInFormEditor && showLocationMap.value;
    });

    const isSubElement = computed(() => {
        return !(element.ClassName == "LocationElement");
    });

    const disableOverrideInterface = computed(() => {
        return !showLocationMap.value && !isSubElement.value;
    });

    const useDirectInput = computed(() => {
        return disableOverrideInterface.value || mapServiceUnavailable.value;
    });

    const locationChanged = computed(() => {
        return !locationObject.value.isEquivalent(overrideValue.value);
    });

    const locationValue = computed(() => {
        if (isModuleRegistered(PROJECT_FORM_NAMESPACE, store.state)) {
            return store.getters[PROJECT_TYPES.getters.dataFieldValueFromId](locationDataFieldId);
        }
        return null;
    });
    const locationObject = computed(() => {
        return ClientLocation.fromServerString(locationValue.value);
    });

    // longitude is x
    const longitudeGetter = computed(() => {
        return locationObject.value.Longitude?.toString() ?? "";
    });
    function longitudeSetter(value) {
        validationHub.validate();
        updateValueFromServiceUnavailableOverride("Longitude", value);
    }
    // latitude is y
    const latitudeGetter = computed(() => {
        return locationObject.value.Latitude?.toString() ?? "";
    });
    function latitudeSetter(value) {
        validationHub.validate();
        updateValueFromServiceUnavailableOverride("Latitude", value);
    }

    const overrideLongitudeGetter = computed(() => {
        return overrideValue.value?.Longitude?.toString() ?? "";
    });
    function overrideLongitudeSetter(value) {
        overrideValue.value.Longitude = value;
        updateMapChosenMarker();
    }

    const overrideLatitudeGetter = computed(() => {
        return overrideValue.value?.Latitude?.toString() ?? "";
    });
    function overrideLatitudeSetter(value) {
        overrideValue.value.Latitude = value;
        updateMapChosenMarker();
    }

    const longitudeValidator = computed(() => {
        const validatorTypes = [
            {
                type: VALIDATOR_TYPES.decimal
            },
            {
                type: VALIDATOR_TYPES.max,
                max: 180,
                overrideMsg: longitudeMaxLengthErrorMsg.value
            },
            {
                type: VALIDATOR_TYPES.min,
                min: -180,
                overrideMsg: longitudeMinLengthErrorMsg.value
            },
            {
                type: VALIDATOR_TYPES.custom,
                validate: (value) => {
                    return validateEntryComplete(LONGITUDE_VALIDATOR_KEY, value);
                },
                errorMsg: longitudeLocationCompleteErrorMsg.value
            }
        ];
        if (element.required && !isInFormEditor) {
            validatorTypes.push({ type: VALIDATOR_TYPES.required });
        }
        return getValidator(app, validatorTypes);
    });

    const latitudeValidator = computed(() => {
        const validatorTypes = [
            {
                type: VALIDATOR_TYPES.decimal
            },
            {
                type: VALIDATOR_TYPES.max,
                max: 90,
                overrideMsg: latitudeMaxLengthErrorMsg.value
            },
            {
                type: VALIDATOR_TYPES.min,
                min: -90,
                overrideMsg: latitudeMinLengthErrorMsg.value
            },
            {
                type: VALIDATOR_TYPES.custom,
                validate: (value) => {
                    return validateEntryComplete(LATITUDE_VALIDATOR_KEY, value);
                },
                errorMsg: latitudeLocationCompleteErrorMsg.value
            }
        ];
        if (element.required && !isInFormEditor) {
            validatorTypes.push({ type: VALIDATOR_TYPES.required });
        }
        return getValidator(app, validatorTypes);
    });

    const longitudeBinder = computed(() => {
        return {
            label: useDirectInput.value ? longitudeLabel.value : "",
            placeholder: useDirectInput.value ? "" : longitudeLabel.value,
            altLabel: useDirectInput.value ? "" : longitudeLabel.value,
            readonly: overrideReadonly.value,
            showValidationErrors: useDirectInput.value,
            validator: longitudeValidator.value,
            inputmode: "decimal",
            valueProp: "longitude"
        };
    });
    const latitudeBinder = computed(() => {
        return {
            label: useDirectInput.value ? latitudeLabel.value : "",
            placeholder: useDirectInput.value ? "" : latitudeLabel.value,
            altLabel: useDirectInput.value ? "" : latitudeLabel.value,
            readonly: overrideReadonly.value,
            showValidationErrors: useDirectInput.value,
            validator: latitudeValidator.value,
            inputmode: "decimal",
            valueProp: "latitude"
        };
    });

    const showInvitationToEdit = computed(() => {
        return locationObject.value.isEmpty && !mapServiceUnavailable.value && !overrideReadonly.value;
    });

    const disableAcceptButton = computed(() => {
        return !locationChanged.value || validationErrors.value.length > 0;
    });

    const locationDataModel = computed(() => {
        return {
            locationObject: locationObject.value,
            longitudeGetter: longitudeGetter.value,
            latitudeGetter: latitudeGetter.value,
            overrideValue: overrideValue.value,
            retrievingSasToken: retrievingSasToken.value,
            mapConfig: mapConfig.value,
            useDirectInput: useDirectInput.value,
            editingOverrideManually: editingOverrideManually.value,
            editingOverrideViaMap: editingOverrideViaMap.value,
            overrideLongitudeGetter: overrideLongitudeGetter.value,
            overrideLatitudeGetter: overrideLatitudeGetter.value,
            longitudeBinder: longitudeBinder.value,
            latitudeBinder: latitudeBinder.value,
            locationValidationErrors: validationErrors.value,
            locationChanged: locationChanged.value,
            showInvitationToEdit: showInvitationToEdit.value,
            disableAcceptButton: disableAcceptButton.value,
            showDependencyOverlay: showDependencyOverlay.value,
            showLocationMap: showLocationMap.value,
            usingLocationMap: usingLocationMap.value,
            dependencyAlertBinder: dependencyAlertBinder.value,
            dependencyLoaderMessage: dependencyLoaderMessage.value,
            invitationToEditMessage: invitationToEditMessage.value,
            validationHub: validationHub
        };
    });

    watch(locationValue, (newValue, oldValue) => {
        if (newValue !== oldValue) {
            if (!newValue) {
                clearSelectedMarker();
            } else {
                updateSelectedMarker(locationObject.value);
            }
        }
    });

    //validation methods
    const validationErrors = computed(() => {
        const locationValidationErrors = [];
        if (validationResults.value.manualLong && validationResults.value.manualLong.hasError) {
            locationValidationErrors.push(validationResults.value.manualLong.error);
        }
        if (validationResults.value.manualLat && validationResults.value.manualLat.hasError) {
            locationValidationErrors.push(validationResults.value.manualLat.error);
        }
        return locationValidationErrors;
    });

    function onLocationValidated(prop, validationResult) {
        validationResults.value[prop] = validationResult;
    }

    function validateEntryComplete(validatorType, value) {
        const latitudeValue =
            !useDirectInput.value && locationChanged.value ? overrideLatitudeGetter.value : latitudeGetter.value;
        const longitudeValue =
            !useDirectInput.value && locationChanged.value ? overrideLongitudeGetter.value : longitudeGetter.value;
        if (validatorType == LATITUDE_VALIDATOR_KEY) {
            return (
                (latitudeValue === "" && longitudeValue === "" && value === "") ||
                (latitudeValue !== "" && value !== "")
            );
        } else {
            return (
                (latitudeValue === "" && longitudeValue === "" && value === "") ||
                (longitudeValue !== "" && value !== "")
            );
        }
    }
    // end validation

    // Manual entry
    function openManualOverride() {
        editingOverrideManually.value = true;
        initializeOverrideValue();
    }
    function acceptManualOverride() {
        if (
            validationErrors.value.length > 0 ||
            (!overrideValue.value.isFullyDefined && !overrideValue.value.isEmpty)
        ) {
            // Can't accept incomplete location
            return;
        }
        editingOverrideManually.value = false;
        editingOverrideViaMap.value = false;
        if (overrideValue.value.isEmpty) {
            // Acceptable to clear data entirely
            //replace with locationid
            sendLocationDataChange(locationDataFieldId, "");
        } else {
            sendLocationDataChange(locationDataFieldId, overrideValue.value.toServerString());
        }
        clearMapChosenMarker();
    }
    function cancelManualOverride() {
        editingOverrideManually.value = false;
        editingOverrideViaMap.value = false;
        initializeOverrideValue();
        clearMapChosenMarker();
    }
    function initializeOverrideValue() {
        overrideValue.value = locationObject.value.clone();
        validationResults.value.manualLat = null;
        validationResults.value.manualLong = null;
    }

    // Map operations
    function setInitialLocation() {
        if (locationObject.value.isFullyDefined) {
            mapEmitter.emit("update-data-marker", locationObject.value);
        }
    }
    function updateLocationFromMap(chosenLocation) {
        if (overrideReadonly.value) {
            return;
        }

        if (!editingOverrideViaMap.value) {
            editingOverrideViaMap.value = true;
            initializeOverrideValue();
        }

        overrideValue.value.Latitude = chosenLocation.Latitude;
        overrideValue.value.Longitude = chosenLocation.Longitude;
        updateMapChosenMarker();
    }
    function updateSelectedMarker(newLocation) {
        mapEmitter.emit("update-data-marker", newLocation);
    }
    function clearSelectedMarker() {
        mapEmitter.emit("clear-data-marker");
    }
    function updateValueFromServiceUnavailableOverride(property, newValue) {
        const newLocation = locationObject.value.clone();
        newLocation[property] = newValue;
        sendLocationDataChange(locationDataFieldId, newLocation.toServerString());
    }
    function updateMapChosenMarker() {
        if (overrideValue.value.isValid) {
            mapEmitter.emit("update-map-chosen-marker", overrideValue.value);
        } else {
            clearMapChosenMarker();
        }
    }
    function clearMapChosenMarker() {
        mapEmitter.emit("clear-map-chosen-marker");
    }
    function setupMapConfig() {
        const referenceCenter =
            element && element.AuxiliaryModel
                ? ClientLocation.fromServerString(element.AuxiliaryModel.ReferenceCenter)
                : null;
        mapConfig.value = GetLocationConfig(sasToken.value, mapEmitter, referenceCenter, locationObject.value);
    }
    function onInitializeFailed() {
        mapServiceUnavailable.value = true;
    }

    function sendLocationDataChange(dataFieldId, value) {
        if (isModuleRegistered(PROJECT_FORM_NAMESPACE, store.state)) {
            store.dispatch(
                PROJECT_FORM_TYPES.actions.setProjectData,
                {
                    dataFieldId,
                    value
                },
                { root: true }
            );
        }
    }

    const initializeMapConfig = async () => {
        if (!useDirectInput.value) {
            await updateSasToken();
            // if EnableLocationMap is true
            if (showLocationMap.value) {
                setupMapConfig();
            }

            // before the sas token is cached, the initialize may occur after the dependency has changed.
            // if that's the case, attempt to autogeocode
            if (isSubElement.value && !locationValue.value) {
                autoGeocodeFromDependency(false);
            }
        }
    };

    const reinitializeMapConfig = async () => {
        mapServiceUnavailable.value = false;
        const sasTokenRetrieved = await updateSasToken();
        if (sasTokenRetrieved) {
            mapEmitter.emit("update-sas-token", sasToken.value);
        } else {
            mapServiceUnavailable.value = true;
        }
    };

    const updateSasToken = async () => {
        // Skip getting the sas token if this is not on the EditProject page
        if (!isModuleRegistered(PROJECT_FORM_NAMESPACE, store.state)) {
            return;
        }
        retrievingSasToken.value = true;
        try {
            sasToken.value = await store.dispatch(
                PROJECT_FORM_CONTROLLER_TYPES.actions.getLocationSasToken,
                {},
                { root: true }
            );
        } catch (e) {
            return false;
        } finally {
            retrievingSasToken.value = false;
        }
        return true;
    };

    const showDependencyOverlay = computed(() => {
        return (
            isSubElement.value &&
            !overrideReadonly.value &&
            !retrievingSasToken.value &&
            !locationValue.value &&
            !suppressOverlay.value
        );
    });

    const dependencyAlertBinder = computed(() => {
        return {
            // boolean call may need to be changed
            shown: dependencyChanged.value && Boolean(locationValue.value),
            severity: ALERT_SEVERITY.warning
        };
    });
    const parentElementLabel = computed(() => {
        return isSubElement.value ? element.Properties[ELEMENT_PROPERTY_NAME.Label] : null;
    });
    const dependencyLoaderMessage = computed(() => {
        return parentElementLabel.value ? dependencyLoaderMessageNamed.value : dependencyLoaderMessageText.value;
    });

    const invitationToEditMessage = computed(() => {
        return isInFormEditor ? invitationToEditTextTextInFormEditor.value : invitationToEditText.value;
    });

    function DependencyChange() {
        if (locationValue.value) {
            // this is used to determine when to display the "regeocode" alert, and whether to geolocate
            // from the "locate now" button
            dependencyChanged.value = true;
        } else {
            debouncedAutoGeocode(true);
        }
    }

    // Geocoding
    function autoGeocodeFromDependency(setDependencyChangedOnIncomplete) {
        if (IsAddressCompleteEnoughForAutoGeocode(app, store.getters, element.PublicId)) {
            geocodeFromDependency();
        } else if (setDependencyChangedOnIncomplete) {
            // if the address is not complete enough for auto-geocoding, just record that the dependency changed
            dependencyChanged.value = true;
        }
    }

    //exported, handle in locationSubElementLayout
    function overlayGeocodeFromDependency() {
        suppressOverlay.value = true;
        if (dependencyChanged.value) {
            geocodeFromDependency();
        }
    }

    const geocodeFromDependency = async () => {
        if (mapServiceUnavailable.value || retrievingSasToken.value) {
            return;
        }

        // first, generate the address
        const address = GetAddressFromDependency(app, store.getters, element.PublicId);

        let newLocation = null;
        if (address) {
            // second, geocode using that address
            newLocation = await geocodeAddressUsingAzureMaps(app, address, sasToken.value);
        }

        if (newLocation) {
            sendLocationDataChange(locationDataFieldId, newLocation.toServerString());
        } else {
            // if the geocoding fails, we'll just clear the location
            sendLocationDataChange(locationDataFieldId, "");
        }
        // replace, mark reference boolean as false (until the next time someone makes changes)
        dependencyChanged.value = false;
    };

    // RETURN DATAMODEL + FUNCTIONS THAT ARE NEEDED TO CALL
    return {
        //Data model
        locationDataModel,
        //Functions
        onLocationValidated,
        openManualOverride,
        acceptManualOverride,
        cancelManualOverride,
        onInitializeFailed,
        sendLocationDataChange,
        longitudeSetter,
        latitudeSetter,
        overrideLongitudeSetter,
        overrideLatitudeSetter,
        DependencyChange,
        overlayGeocodeFromDependency,
        //Async methods
        initializeMapConfig,
        reinitializeMapConfig,
        geocodeFromDependency,
        //i18n keys
        dependencyChangedAlertText,
        dependencyChangedButtonLabel,
        locateNowButtonLabel
    };
}
